import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import { accessApi } from '../services/accessApi';
import { RotatingAccessQr } from '../components/RotatingAccessQr';

/**
 * MyAccessPass (Jaime 2026-10-17): PRIVATE full-screen route (/home/access-pass)
 * that renders ONLY the resident's rotating VQR2 user-identity pass — no
 * header, no tab bar, no navigation chrome — sized for a clean 9:16 phone
 * screenshot / screen recording. Reuses the exact same data source
 * (GET /qr-access/my-pass) and the shared RotatingAccessQr component as the
 * "Mi pase de acceso" card on /home/my-qr; same 60s rotation and countdown.
 * Brand backdrop: deep teal gradient from the design tokens.
 */
export default function MyAccessPass() {
  const [userPass, setUserPass] = useState<{ user_id: string; qr_secret: string } | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'none'>('loading');

  useEffect(() => {
    let alive = true;
    setState('loading');
    accessApi.qrAccess.myPass()
      .then((res) => {
        if (!alive) return;
        const d = res.data?.data;
        if (d?.user_id && d?.qr_secret) {
          setUserPass({ user_id: d.user_id, qr_secret: d.qr_secret });
          setState('ready');
        } else {
          setUserPass(null);
          setState('none');
        }
      })
      .catch(() => {
        if (alive) { setUserPass(null); setState('none'); }
      });
    return () => { alive = false; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-6 overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, var(--v-teal-800) 0%, var(--v-teal-900) 60%, #021b1e 100%)',
      }}
    >
      <div className="flex items-center gap-2 text-[var(--v-teal-200)]">
        <ShieldCheck className="w-5 h-5" />
        <span className="text-sm font-medium tracking-wide uppercase">Pase de acceso</span>
      </div>

      {state === 'loading' ? (
        <Loader2 className="w-8 h-8 animate-spin text-[var(--v-teal-300)]" />
      ) : userPass ? (
        <div className="p-5 rounded-3xl bg-white/95 shadow-2xl">
          <RotatingAccessQr
            variant="vqr2"
            userId={userPass.user_id}
            code={userPass.user_id}
            qrSecret={userPass.qr_secret}
            size={288}
            tone="light"
          />
        </div>
      ) : (
        <p className="text-sm text-[var(--v-teal-200)] text-center px-8">
          Pase no disponible para esta cuenta.
        </p>
      )}

      <p className="text-xs text-[var(--v-teal-200)]/70">
        Rota cada minuto · caseta verifica al escanear
      </p>
    </div>
  );
}
